import * as d3 from 'd3';
import { isValid, makeAbsoluteContext, determinePositionForAngle } from '../chartUtils';
import { readableColor } from 'polished';
import { intersectsAny } from 'utilities/utils';

const TEXT_SIZE = 10;

/**
 * @param svg
 * @param updateTooltip
 * @returns {{clear: clear, update: externalUpdate}}
 * @constructor
 */
export const PolarAreaChart = ({ container: svg, updateTooltip }) => {
	let initialized = false;

	// define x -> around the circle
	const xScale = d3
		.scaleBand()
		.range([0, 2 * Math.PI])
		.align(0);

	// define y -> out from the center
	const yScale = d3.scaleRadial();

	// define the arc renderer
	const arc = d3
		.arc()
		.innerRadius((d) => yScale(d[0]))
		.outerRadius((d) => yScale(d[1]))
		.startAngle((d) => xScale(d.data.label))
		.endAngle((d) => xScale(d.data.label) + xScale.bandwidth())
		.padAngle(0.01);

	// define the arc renderer
	const fullArc = d3
		.arc()
		.startAngle((d) => xScale(d))
		.endAngle((d) => xScale(d) + xScale.bandwidth())
		.padAngle(0.01);

	// define the arc renderer
	const labelArc = d3
		.arc()
		.startAngle((d) => xScale(d))
		.endAngle((d) => xScale(d) + xScale.bandwidth())
		.padAngle(0.01);

	// define the arc renderer
	const tooltipArc = d3
		.arc()
		.startAngle((d) => xScale(d))
		.endAngle((d) => xScale(d) + xScale.bandwidth())
		.padAngle(0.01);

	// manage the initialization of the chart, should only be called once
	function init() {
		d3.select(svg).style('width', '100%').style('height', 'auto');

		d3.select(svg).append('g').classed('back', true);
	}

	function update({ data, properties, keys, extent, colorPropertyMap }, { height, width }, { colorScale }) {
		const outerRadiusMax = Math.floor(Math.min(width, height)) / 2 - 4 * TEXT_SIZE;
		const innerRadiusMin = Math.min(50, Math.floor(outerRadiusMax / 3));

		xScale.domain(properties);
		yScale.domain([0, extent[1]]).range([innerRadiusMin, outerRadiusMax]);

		arc
			.padRadius(innerRadiusMin)
			.innerRadius((d) => yScale(d[0]))
			.outerRadius((d) => yScale(d[1]))
			.startAngle((d) => xScale(d.data.label))
			.endAngle((d) => xScale(d.data.label) + xScale.bandwidth());
		fullArc
			.padRadius(innerRadiusMin)
			.innerRadius(() => yScale(0))
			.outerRadius(() => yScale(extent[1]))
			.startAngle((d) => xScale(d))
			.endAngle((d) => xScale(d) + xScale.bandwidth());
		labelArc
			.padRadius(innerRadiusMin)
			.innerRadius(() => yScale(extent[1]))
			.outerRadius(() => yScale(extent[1]))
			.startAngle((d) => xScale(d))
			.endAngle((d) => xScale(d) + xScale.bandwidth());
		tooltipArc
			.padRadius(innerRadiusMin)
			.innerRadius((d) => yScale(d[1]))
			.outerRadius((d) => yScale(d[1]))
			.startAngle((d) => xScale(d.data.label))
			.endAngle((d) => xScale(d.data.label) + xScale.bandwidth());

		// greyed out backgrounds arc to help with scale
		d3.select(svg)
			.select('.back')
			.selectAll('g.full')
			.data(properties, (d) => d)
			.join(
				(enter) => {
					const g = enter.append('g').classed('full', true);

					g.append('path').attr('d', fullArc).attr('fill', 'lightgrey').attr('opacity', 0.3);

					g.append('text')
						.style('text-anchor', (d) => (xScale(d) < Math.PI ? 'start' : 'end'))
						.style('alignment-baseline', (d) => {
							let midAngle = xScale(d);
							return midAngle > Math.PI / 2 && midAngle <= (3 * Math.PI) / 2 ? 'hanging' : 'ideographic';
						})
						.attr('transform', (d) => {
							let coordinates = labelArc.centroid(d);
							// add spacing between the text labels
							coordinates[0] += coordinates[0] > 0 ? 10 : -10;
							coordinates[1] += coordinates[1] > 0 ? 10 : -10;
							return 'translate(' + coordinates + ')';
						})
						.attr('cy-test', 'polar-data-arc-label')
						.text((d) => d);

					return g;
				},
				(update) => {
					update.selectAll('path').attr('d', (d) => fullArc(d));
					update
						.selectAll('text')
						.style('text-anchor', (d) => (xScale(d) < Math.PI ? 'start' : 'end'))
						.style('alignment-baseline', (d) => {
							let midAngle = xScale(d);
							return midAngle > Math.PI / 2 && midAngle <= (3 * Math.PI) / 2 ? 'hanging' : 'ideographic';
						})
						.attr('transform', (d) => {
							let coordinates = labelArc.centroid(d);
							// add spacing between the text labels and graph
							coordinates[0] += coordinates[0] > 0 ? 10 : -10;
							coordinates[1] += coordinates[1] > 0 ? 10 : -10;
							return 'translate(' + coordinates + ')';
						})
						.text((d) => d);

					return update;
				},
				(exit) => exit.remove()
			);

		const labelPositionList = [];
		d3.select(svg)
			.select('.back')
			.selectAll('g.full text')
			.attr('font-size', TEXT_SIZE)
			.each(function (d) {
				var pos = labelArc.centroid(d);

				var self = d3.select(this);
				const bbox = self.node().getBBox();

				const rectangle = { height: bbox.height, width: bbox.width, x: pos[0], y: pos[1] };

				if (intersectsAny(rectangle, labelPositionList)) {
					d3.select(this).attr('hidden', true);
				} else {
					labelPositionList.push(rectangle);
				}
			});

		// primary bands
		d3.select(svg)
			.selectAll('g.band')
			.data(d3.stack().keys(keys)(data), (d) => d)
			.join(
				(enter) => enter.append('g').classed('band', true).attr('opacity', 1).attr('cy-test', 'polar-data-band'),
				(update) => update,
				(exit) => exit.remove()
			)
			.selectAll('g.arc')
			.data((d) => d)
			.join(
				(enter) => {
					const g = enter.append('g').classed('arc', true).attr('cy-test', 'polar-data-arc');

					g.append('path')
						.attr('d', arc)
						.attr('opacity', 1.0)
						.attr('fill', function () {
							const group = this.parentNode.parentNode;
							const parentKey = d3.select(group).datum().key;
							return colorScale(colorPropertyMap[parentKey]);
						})
						.attr('cy-test', 'polar-data-path')
						.on('mouseover', function (event, d) {
							d3.select(svg).selectAll('.arc path').transition().duration(150).attr('opacity', 0.5);

							d3.select(this).transition().duration(150).attr('opacity', 1.0);

							// clear the tooltip first just in case
							// if we move from arc to arc is wasn't resetting in between
							updateTooltip(null, null, null);

							handleTooltip(event, d, updateTooltip, tooltipArc, xScale, svg, this.parentNode);
						})
						.on('mouseout', function () {
							d3.select(svg).selectAll('.arc path').transition().duration(150).attr('opacity', 1.0);
							updateTooltip(null, null, null);
						});

					// label of count internal to the arcs
					g.append('text')
						.attr('font-size', TEXT_SIZE)
						.attr('pointer-events', 'none')
						.style('text-anchor', 'middle')
						.attr('fill', function () {
							const arc = this.parentNode;
							const group = arc.parentNode;
							const color = colorScale(colorPropertyMap[d3.select(group).datum().key]);
							return color ? readableColor(color) : 'black';
						})
						.style('dominant-baseline', 'middle')
						.attr('transform', (d) => (isValid(arc.centroid(d)) ? `translate(${arc.centroid(d)})` : null))
						.text(function (d) {
							const arc = this.parentNode;
							const group = arc.parentNode;
							const key = d3.select(group).datum().key;
							return d.data[key];
						})
						.attr('opacity', function (d) {
							const deltaY = yScale(d[1]) - yScale(d[0]);
							const textBox = d3.select(this).node().getBBox();
							return deltaY > textBox.width && deltaY > textBox.height ? 1 : 0;
						});

					return g;
				},
				(update) => {
					update
						.select('path')
						.attr('d', arc)
						.attr('fill', function () {
							const group = this.parentNode.parentNode;
							const parentKey = d3.select(group).datum().key;
							return colorScale(colorPropertyMap[parentKey]);
						});

					update
						.select('text')
						.attr('fill', function () {
							const arc = this.parentNode;
							const group = arc.parentNode;
							const color = colorScale(colorPropertyMap[d3.select(group).datum().key]);
							return color ? readableColor(color) : 'black';
						})
						.attr('transform', (d) => (isValid(arc.centroid(d)) ? `translate(${arc.centroid(d)})` : null))
						.text(function (d) {
							const arc = this.parentNode;
							const group = arc.parentNode;
							const key = d3.select(group).datum().key;
							return d.data[key];
						})
						.attr('opacity', function (d) {
							const deltaY = yScale(d[1]) - yScale(d[0]);
							const textBox = d3.select(this).node().getBBox();
							return deltaY > textBox.width && deltaY > textBox.height ? 1 : 0;
						})
						.attr('cy-test', 'polar-data-arc-value');

					return update;
				},
				(exit) => exit.remove()
			);
	}

	function mouseOver(event, item) {
		d3.select(svg)
			.selectAll('g.band')
			.transition()
			.duration(250)
			.attr('opacity', function () {
				const current = d3.select(this).datum().key;
				return current === item.key ? 1.0 : 0.5;
			});
	}

	function mouseOut() {
		d3.select(svg).selectAll('g.band').transition().duration(250).attr('opacity', 1.0);
	}

	function externalUpdate({ data, dimensions, options }) {
		if (!initialized) {
			init(data, dimensions);
			initialized = true;
		}

		// bring back hidden labels before checking for overlap
		d3.select(svg).select('.back').selectAll('g.full text').attr('hidden', null);

		update(data, dimensions, options);
	}

	function clear() {
		d3.select(svg).selectAll('g').remove();
	}

	return {
		update: externalUpdate,
		clear: clear,
		mouseOver: mouseOver,
		mouseOut: mouseOut,
	};
};

function handleTooltip(event, d, updateTooltip, tooltipArc, xScale, svg, _arc) {
	const group = _arc.parentNode;
	const parentKey = d3.select(group).datum().key;
	const centroid = tooltipArc.centroid(d);

	const angle = (2 * xScale(d.data.label) + xScale.bandwidth()) / 2;
	const position = determinePositionForAngle(angle);

	// since the parent group has a translate, we need to transform to global
	const transform = makeAbsoluteContext(d3.select(_arc).node(), d3.select(svg).node().parentNode);

	const transformed = transform(centroid[0], centroid[1]);

	const data = {
		title: d.data.label,
		subtitle: parentKey,
		count: d.data[parentKey],
	};

	updateTooltip(
		data,
		{
			x: transformed.x,
			y: transformed.y - 15,
		},
		{ position: position }
	);
}
